import React, { ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock } from "lucide-react";
import posthog from "posthog-js";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { addRecent } from "@/lib/utils/recents";

interface PageSidebarItemProps {
	icon: ReactNode;
	title: string;
	path: string;
	expanded: boolean;
	itemKey?: string;
	onInteraction?: () => void;
	onBeforeNavigate?: () => boolean;
	isLocked?: boolean;
	onLockedClick?: () => void;
	suppressHighlight?: boolean;
	badgeCount?: number;
	badgeLabel?: string;
}

export const PageSidebarItem: React.FC<PageSidebarItemProps> = ({
	icon,
	title,
	path,
	expanded,
	itemKey,
	onInteraction,
	onBeforeNavigate,
	isLocked = false,
	onLockedClick,
	suppressHighlight = false,
	badgeCount = 0,
	badgeLabel,
}) => {
	const navigate = useNavigate();
	const location = useLocation();

	const isSelected =
		!suppressHighlight &&
		(path.split("/").length > 2
			? location.pathname === path ||
				location.pathname.startsWith(path + "/")
			: location.pathname.split("/")[1] === path.split("/")[1]);

	const handleClick = () => {
		if (isLocked) {
			onLockedClick?.();
			return;
		}

		if (onBeforeNavigate && !onBeforeNavigate()) return;

		posthog.capture("sidebar_navigate", {
			page: title,
			path,
		});
		if (itemKey) {
			addRecent({
				type: "page",
				key: itemKey,
				title,
				path,
				timestamp: Date.now(),
			});
		}
		onInteraction?.();
		navigate(path);
	};

	return (
		<div
			onClick={handleClick}
			className={cn(
				"flex items-center gap-2 pl-2 py-1 rounded-md cursor-pointer transition-colors duration-200 text-gray-700",
				expanded ? "justify-start pl-2" : "justify-center px-0",
				isSelected ? "bg-stone-300 font-medium" : "hover:bg-stone-300"
			)}
			data-testid="page-sidebar-item"
			aria-label={isLocked ? `${title} (gesperrt)` : title}
		>
			<div className="relative w-[22px] h-[22px] flex items-center justify-center">
				{icon}
				{!expanded && badgeCount > 0 && (
					<span className="absolute right-0 top-0 h-2 w-2 rounded-full bg-red-500" />
				)}
			</div>

			{expanded && (
				<span className="text-sm font-medium truncate flex-1">
					{title}
				</span>
			)}

			{expanded && badgeCount > 0 && (
				<span
					className="mr-2 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white"
					aria-label={badgeLabel}
					data-testid={`sidebar-badge-${itemKey}`}
				>
					{badgeCount}
				</span>
			)}

			{expanded && isLocked && (
				<Lock className="w-3.5 h-3.5 text-gray-400 mr-2" />
			)}
		</div>
	);
};
